import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { paths, routes } from '../../../routes';
import { BenchmarkTypesModal, Select } from 'components';
import { Button, DropdownButton, Option } from '../../../components';
import { Info } from '../../../components/Icons';
import { useTable } from './hooks';
import lookup from 'api/lookup';
import { SelectColumnFilter } from 'components/Table/helpers/SelectColumnFilter/SelectColumnFilter';
import { useDefaultLagDate } from 'hooks/useDefaultLagDate';
import { ControlsButtons, TableWrapper } from './BenchmarkListStyles';
import Refresh from 'components/Icons/Refresh';
import ControlledPaginationTable from 'components/ControlledPaginationTable/ControlledPaginationTable';
import {
  KEY as pageIndexSearchParamKey,
  usePageSearchParam,
} from 'hooks/usePageURLSearchParam/usePageURLSearchParam';
import {
  KEY as pageSizeSearchParamKey,
  usePageSizeSearchParam,
} from 'hooks/usePageSizeURLSearchParam/usePageSizeURLSearchParam';
import { DEFAULT_TABLE_PAGESIZE } from 'components/Table/contants';

const DateFilter = (props: any) => <Select {...props} />;
type Props = {};

export const BenchmarkList: React.FC<Props> = () => {
  const [showBenchmarkTypesModal, setShowBenchmarksTypesModal] =
    useState(false);
  const history = useHistory();
  const [benchmarkOptions, setBenchmarkOptions] = useState<Option[]>([]);

  const { pageIndex } = usePageSearchParam();
  const { pageSize } = usePageSizeSearchParam();

  const { defaultLagDate, isLoading: adminSettingsLoading } =
    useDefaultLagDate();

  const { table, isLoadingBenchmarks, resetTable } = useTable({
    selectColumnFilter: SelectColumnFilter,
    dateFilter: DateFilter,
    defaultLagDate,
    pageIndex,
    pageSize,
  });

  const redirect = (e: any) => {
    if (!e.value) {
      setShowBenchmarksTypesModal(true);
      return;
    }

    const path = e.id
      ? routes.getBenchmarkDetails(e.value, e.id)
      : routes.getBenchmark(e.value);

    history.push({
      pathname: path,
      search: new URLSearchParams({
        [pageIndexSearchParamKey]: `${pageIndex + 1 || 1}`,
        [pageSizeSearchParamKey]: `${pageSize || DEFAULT_TABLE_PAGESIZE}`,
      }).toString(),
    });
  };

  const { data: benchmarkData } = lookup.GetBenchmarkTypes();

  useEffect(() => {
    const types: Option[] =
      benchmarkData?.data?.map((item) => {
        return { title: item.name, value: item.id };
      }) ?? [];
    types.push({
      title: 'Benchmark Descriptions',
      value: '',
      divider: true,
      icon: <Info size='18px' fill='#0076C0' />,
    });
    setBenchmarkOptions(types);
  }, [benchmarkData]);

  return (
    <>
      <TableWrapper data-testid='benchmark-blender-table-container'>
        <ControlledPaginationTable
          data={table.data}
          columns={table.columns}
          isLoading={adminSettingsLoading || isLoadingBenchmarks}
          debug={false}
          actions={
            <ControlsButtons>
              <Button
                variant='tertiary'
                data-testid='benchmarks-reset-table'
                onClick={resetTable}
              >
                <Refresh
                  style={{
                    marginRight: 2,
                    marginTop: 2,
                  }}
                />
                Reset Filters
              </Button>
              <Button
                variant='secondary'
                data-testid='benchmarks-manage-jobs-button-container'
                onClick={() => history.push(paths.manageJobs)}
              >
                Manage Jobs
              </Button>
              <DropdownButton list={benchmarkOptions} onSelect={redirect}>
                Add Benchmark
              </DropdownButton>
            </ControlsButtons>
          }
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={Math.ceil(table.data.length / pageSize)}
          selector={'#benchmarks'}
          globalSearchFiltersProperties={{
            enabled: true,
            testId: 'benchmark-blender-search-input',
          }}
        />
      </TableWrapper>
      <BenchmarkTypesModal
        show={showBenchmarkTypesModal}
        onClose={() => setShowBenchmarksTypesModal(false)}
      />
    </>
  );
};
